<template>
    <DxPopover :target="props.bindTarget" show-event="click" :hide-on-outside-click="true" @shown="onPopoverShown" @hidden="onPopoverHidden">
        <div class="ask-chat-popover">
            <DxTabs :selected-index="activeChatTab" @item-click="onTabClick">
                <DxItem badge="Data" />
                <DxItem badge="Files" />
            </DxTabs>
            <DxChat ref="chatComponent" :width="chatWidth" :height="chatHeight" :items="chatMessages" :user="chatUser" message-template="message" :typingUsers="chatTypingUsers" :day-header-format="chatDayHeaderFormat" :alerts="chatAlerts" @message-entered="messageEntered">
                <template #message="{ data }">
                    <div v-if="data.message && data.message.sources && Array.isArray(data.message.sources) && data.message.sources.length > 0">
                        <div class="chat-message-sources">
                            <span class="sources-label">Used sources:</span>
                            <span class="source-chip" v-for="sourceItem in data.message.sources" :key="sourceItem.chunk_id">
                                <a href="#" target="_blank" rel="noopener" @click.prevent="downloadSource(sourceItem)">{{ sourceItem.filename || sourceItem.doc_id }}</a>
                            </span>
                        </div>
                    </div>
                    <div v-if="data.message.author.id != chatUser.id && data.message.messageType == 'error'" class="markdown-chat-message">
                        <div class="dx-error-message reduce-padding-more"><i class="dx-icon-warning"></i><div class="dx-error-message-text">{{ data.message.text }}</div></div>
                        <div v-if="showDebugUI">
                            <a v-if="!messageDebugging[data.message.id]" class="chat-debug-toggle" href="#" @click.prevent="messageDebugging[data.message.id] = !messageDebugging[data.message.id]">(show debug)</a>
                            <div v-else>
                                <div class="chat-debug-query">SQL: {{ data.message.query || '(no query available)' }}</div>
                                <div class="chat-debug-full-response"><b>Full Response:</b><br>{{ data.message.fullResponse || '(no full response available)' }}</div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="data.message.author.id != chatUser.id" class="markdown-chat-message" v-html="micromark(data.message.text).replace(/^<p>/, '').replace(/<\/p>$/, '')"></div>
                    <div v-else class="markdown-chat-message" v-text="data.message.text"></div>
                    <div v-if="data.message.id == 'post-0'" class="chat-message-hint">
                        <a href="#" @click.prevent="insertHintMessages">(What can I ask about?)</a>
                    </div>
                    <div v-if="data.message.hasResults === 0">
                        <DxButton text="No results found" />
                        <div class="chat-no-results-hint">The AI thinks it understood your question, but didn't find any results.</div>
                    </div>
                    <div v-if="!!data.message.hasResults">
                        <DxButton :text="'Show ' + data.message.hasResults + ' results'" @click="showResultsPopup = data.message.id" />
                        <div v-if="showDebugUI">
                            <a v-if="!messageDebugging[data.message.id]" class="chat-debug-toggle" href="#" @click.prevent="messageDebugging[data.message.id] = !messageDebugging[data.message.id]">(show debug)</a>
                            <div v-else>
                                <div class="chat-debug-query">SQL: {{ data.message.query || '(no query available)' }}</div>
                                <div class="chat-debug-full-response"><b>Full Response:</b><br>{{ data.message.fullResponse || '(no full response available)' }}</div>
                            </div>
                        </div>
                    </div>
                </template>
            </DxChat>
        </div>
    </DxPopover>
    <DxPopup :visible="!!showResultsPopup" :defer-rendering="true" :show-close-button="true" content-template="results-popup-content" @hidden="showResultsPopup = null">
        <template #results-popup-content>
            <ChatResults :results="resultsTables[showResultsPopup]" :key="showResultsPopup || 'empty'" />
        </template>
    </DxPopup>
</template>

<script setup>
/* eslint-disable */
import DxPopover from "devextreme-vue/popover";
import DxPopup from "devextreme-vue/popup";
import DxChat from "devextreme-vue/chat";
import DxTabs, { DxItem } from 'devextreme-vue/tabs';
import DxButton from 'devextreme-vue/button';
import { ref, computed, defineProps, inject, onMounted, onBeforeUnmount } from 'vue';
import { micromark } from 'micromark';
import ChatResults from '@/components/chat-results.vue';
import AppInfo from '@/app-info';
import logoImage from '@/assets/netxene_logo_color.png?url';
import Auth from '@/auth';

const $http = inject('$http');
const props = defineProps({
    // Target element to bind to, e.g. #ask-chat-header-btn
    bindTarget: { type: String, required: true },
    // When true, expand chat ~75% of viewport (capped at 1000px)
    dynamicFocus: { type: Boolean, default: true },
    // Default active tab: 'data' or 'files'
    defaultTab: { type: String, default: 'files', validator: (v) => ['data','files'].includes(v) }
});
const chatComponent = ref(null);
const showDebugUI = computed(() => {
    return Auth.isAdmin.value;
});
const messageDebugging = ref({});           // Map of message IDs to bool true/false if we should show the debug UI for that message. Message is not reactive, so we need to use a ref.

const chatAlerts = ref([]);
const resultsTables = ref({});              // Table of results for each query (mapped by message ID) for populating popup data tables.
const showResultsPopup = ref(null);         // If we should show the results popup, null or the message ID.

const getWelcomeMessage = () => {
    const typeStr = isFilesMode.value ? "files you've uploaded" : "data you've posted";
    return {
        id: 'post-0',
        timestamp: Date.now(),
        author: chatRemoteUser,
        text: `Hello! I'm the AI assistant for ${AppInfo.title}. I can answer questions you have about the **${typeStr}**. Feel free to ask me anything!\n**Note:** I am configured to only answer single questions at a time, with no history of your previous questions.`,
    };
}

const insertHintMessages = () => {
    const msgIdA = 'post-help-' + (chatMessages.value.length + 1);
    const msgIdB = 'post-helper-' + (chatMessages.value.length + 1);
    const msgText = "What can I ask about?";
    const dataReply = "In the data tab, you can ask about the data you've posted. It helps if you are specific about what kind of thing you're looking for, like customers or invoices. I can show you lists of data in a table or a single piece of information." +
        "\n> **Counting**: *How many customers are there?*\n> **Summarizing**: *What did the last customer buy?*\n> **Finding**: *Show me all customers in Germany*\n> **Aggregating**: *List revenues by month for 2025*";
    const filesReply = "In the files tab, you can ask me questions about the files you've uploaded. I can look inside text, documents and pdf files to try to answer, and provide you links to the documents I find." +
        "\n\nFor Example:\n> **Histories**: *What was the last invoice uploaded to Company A?*\n> **Summarizing**: *What is in the file for the invoice posted on 2025-10-01?*\n> **Search**: *What did customer Jane Doe buy?*";
    
    chatMessages.value = [ ...chatMessages.value, {
        id: msgIdA,
        timestamp: Date.now(),
        author: chatUser.value,
        text: msgText,
    }, {
        id: msgIdB,
        timestamp: Date.now(),
        author: chatRemoteUser,
        text: isFilesMode.value ? filesReply : dataReply,
    }];
}

let alertTimeout = null;
const onTabClick = (e) => {
    isFilesMode.value = e.itemIndex == 1;
    // Rerwrite the fake 'welcome' message if we haven't posted anything yet.
    if (chatMessages.value.length == 1 && chatMessages.value[0].id == 'post-0') {
        chatMessages.value = [ getWelcomeMessage() ];
    } else {
        if (alertTimeout)
            clearTimeout(alertTimeout);
        chatAlerts.value = chatAlerts.value.filter(alert => alert.id != 'alert-typeswitch');
        chatAlerts.value.push({ id: 'alert-typeswitch', message: "Now asking questions about " + (isFilesMode.value ? "files" : "data") + "." });
        alertTimeout = setTimeout(() => {
            chatAlerts.value = chatAlerts.value.filter(alert => alert.id != 'alert-typeswitch');
            alertTimeout = null;
        }, 3000);
    }
}

const isFilesMode = ref(props.defaultTab === 'files');             // AI chat has two endpoints, one for files and one for data.
const activeChatTab = computed(() => {
    return isFilesMode.value ? 1 : 0;
})

const chatRemoteUser = { id: 'user-0', name: AppInfo.title, avatarUrl: logoImage };
const chatUser = computed(() => {
    return {
        id: 'user-none',
        name: 'Guest',
    }
});
const chatTypingUsers = ref([]);

const chatMessages = ref([ getWelcomeMessage() ]);

const messageEntered = (e) => {
    chatMessages.value = [ ...chatMessages.value, e.message ];
    chatTypingUsers.value = [ chatRemoteUser ];
    $http.post('/chat', {
        message: e.message.text,
        type: isFilesMode.value ? 'file' : 'data',
    }).then((response) => {
        console.log(response.data);
        const newMessageId = 'post-' + (chatMessages.value.length + 1);
        chatTypingUsers.value = [];
        let messageText = response.data.error ? response.data.error : (response.data.response || '');
        let hasResults = false;
        if (response.data.results) {
            // We expect an array of objects, each with column names as keys. The column names might be completely unique, such as COUNT(*) or aggregation functions.
            if (!Array.isArray(response.data.results)) {
                response.data.error = 'Invalid results format from AI response';
            } else {
                resultsTables.value[newMessageId] = response.data.results;                
                hasResults = response.data.results.length;
            }
        }
        chatMessages.value = [ ...chatMessages.value, {
            id: newMessageId,
            timestamp: Date.now(),
            author: { id: 'user-0', name: AppInfo.title, avatarUrl: logoImage },
            text: messageText,
            messageType: response.data.error ? 'error' : 'response',
            query: response.data.query || '',
            fullResponse: response.data.full || '',
            hasResults: hasResults,
            sources: response.data.sources || null,
        } ];
    }).catch((error) => {
        console.error(error);
        chatTypingUsers.value = [];
    })
}
const chatDayHeaderFormat = 'yyyy/MM/dd';

// Dynamic sizing for chat
const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0);
const viewportHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 0);
const updateViewport = () => {
    if (typeof window === 'undefined') return;
    viewportWidth.value = window.innerWidth;
    viewportHeight.value = window.innerHeight;
};
onMounted(() => {
    updateViewport();
    window.addEventListener('resize', updateViewport);
});
onBeforeUnmount(() => {
    window.removeEventListener('resize', updateViewport);
    stopReadycheck();
});
const chatWidth = computed(() => {
    if (!props.dynamicFocus) return 450;
    return Math.min(1000, Math.round(viewportWidth.value * 0.75));
});
const chatHeight = computed(() => {
    if (!props.dynamicFocus) return 500;
    return Math.min(1000, Math.round(viewportHeight.value * 0.75));
});

// Readycheck polling (runs only while popover is open)
let readycheckInterval = null;
const readycheckTick = async () => {
    try {
        const response = await $http.get('/chat/readycheck');
        console.log('readycheck', response.data);
        // Preserve other alerts; replace only our own readycheck alert
        chatAlerts.value = chatAlerts.value.filter(alert => alert.id != 'alert-readycheck');
        const items = Array.isArray(response.data) ? response.data : [];
        if (items.length > 0) {
            const filenames = items.map(x => x && x.filename).filter(Boolean);
            const listText = filenames.length > 0 ? filenames.join(', ') : '(unnamed files)';
            chatAlerts.value.push({
                id: 'alert-readycheck',
                message: 'The following files have not finished processing by the AI: ' + listText + '.',
            });
        }
    } catch (err) {
        console.error('readycheck failed', err);
    }
};
const startReadycheck = () => {
    if (readycheckInterval) return;
    // Run immediately once, then every 10 seconds
    readycheckTick();
    readycheckInterval = setInterval(readycheckTick, 10000);
};
const stopReadycheck = () => {
    if (readycheckInterval) {
        clearInterval(readycheckInterval);
        readycheckInterval = null;
    }
};
const onPopoverShown = () => {
    startReadycheck();
};
const onPopoverHidden = () => {
    stopReadycheck();
};

// Download a source file from the chat
const downloadSource = async (sourceItem) => {
    try {
        const response = await $http.get(sourceItem.href, { responseType: 'blob' });
        let filename = sourceItem.filename || sourceItem.doc_id || 'download';
        const cd = response && response.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition']);
        if (cd) {
            const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
            const headerName = match && (match[1] || match[2]);
            if (headerName) {
                try { filename = decodeURIComponent(headerName); } catch (_) { filename = headerName; }
            }
        }
        const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (err) {
        console.error('Download failed', err);
    }
}

</script>

<style lang="scss">
.markdown-chat-message {
	h1, h2, h3, h4, h5, h6 {
		margin-top: 4px;
		margin-bottom: 4px;
	}
	h1 { font-size: 1.5em; }
	h2 { font-size: 1.4em; }
	h3 { font-size: 1.3em; }
	h4 { font-size: 1.2em; }
	h5 { font-size: 1.1em; }
	h6 { font-size: 1.0em; }
	p {
		margin-top: 4px;
		margin-bottom: 4px;
	}
	pre {
		overflow-x: scroll;
		padding-bottom: 5px;
		max-width: 300px;
	}
    ul, ol {
        margin-top: 4px;
        margin-bottom: 4px;
        white-space-collapse: collapse;
        li {
            white-space-collapse: break-spaces;
        }
    }
    blockquote {
        margin: 4px 0 4px -4px;
        padding: 0 0 0 8px;
        border-left: 4px solid #ccc;
        white-space-collapse: collapse;
        > * {
            white-space-collapse: break-spaces;
        }
    }
}
.chat-message-hint {
    font-size: 0.9em;
    margin-top: 5px;
}
.chat-debug-toggle {
    display: inline-block;
    width: 100%;
    text-align: right;
    font-size: 0.9em;
    margin-top: 5px;
}
.chat-debug-query {
    margin-top: 5px;
    font-size: 0.9em;
    font-style: italic;
    color: #666;
}
.chat-debug-full-response {
    margin-top: 5px;
    font-size: 0.9em;
    font-style: italic;
    color: #666;
    border: 1px solid #ccc;
    padding: 5px;
    height: 100px;
    overflow-y: scroll;
}
.chat-no-results-hint {
    margin-top: 5px;
    margin-left: 2px;
    font-style: italic;
    font-size: 0.8rem;
}

.chat-message-sources {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 6px;
	margin: 4px 0;
	font-size: 0.9em;
}
.chat-message-sources .sources-label {
	color: #666;
	margin-right: 2px;
}
.source-chip {
	display: inline-flex;
	align-items: center;
	background: #f4f6f8;
	border: 1px solid #dfe3e8;
	border-radius: 999px;
	padding: 2px 8px;
	line-height: 1.6;
	white-space: nowrap;
}
.source-chip a {
	color: #1f6feb;
	text-decoration: none;
}
.source-chip a:hover {
	text-decoration: underline;
}
</style>
